import { trustHTML } from '@ember/template';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { local } from 'embroider-css-modules';

import type { PlottedPoint } from '../../../types/components/prism/data-bar.ts';
import styles from './data-bar-plotted-points.module.css';

type DataBarPoint = {
  isMainPoint: boolean;
  position: number;
  style: string;
  value: number;
};

interface PrismDataBarDataBarPlottedPointsSignature {
  Args: {
    data: PlottedPoint[];
    isAbsolute?: boolean;
    valueFormatter?: (value: number) => string;
  };
  Element: HTMLDivElement;
}

export default class PrismDataBarDataBarPlottedPoints extends Component<PrismDataBarDataBarPlottedPointsSignature> {
  get dataBarPoints(): DataBarPoint[] {
    const { isAbsolute } = this.args;
    const { sortedData } = this;

    const positions: Record<string, PlottedPoint[]> = {};

    sortedData.forEach((datum) => {
      const key = `${datum.value}`;

      if (key in positions) {
        positions[key]!.push(datum);
      } else {
        positions[key] = [datum];
      }
    });

    const dataBarPoints: DataBarPoint[] = [];

    if (sortedData.length === 0) {
      return dataBarPoints;
    }

    // Since multiple positions can be the same, we need to store an offset to add to the other ones
    let offsetPosition = 0;

    // Since we want to group similar values on the progress bar, we'll need to calculate the total offset
    // to do this we need 2 things:
    // - the maximum value
    // - the total amount of duplicate values or the totalOffset
    let totalOffset = 0;

    // The min and max can just be taken out of the sorted array
    const min = sortedData.at(0)?.value ?? 0;
    const max = sortedData.at(-1)?.value ?? 0;

    Object.values(positions).forEach((plottedPoints) => {
      // We multiply by two since the offset between items is 2%
      totalOffset += (plottedPoints.length - 1) * 2;
    });

    // Use 0 as reference value if min > 0 and 5(%) as reference if max < 5(%) (same as for minValue and maxValue)
    let defaultMaxValue = isAbsolute ? 5 : 0.05;

    if (min < 0) {
      defaultMaxValue = 0; // maxValue=0 is allowed if we are dealing with negative numbers (POS)
    }

    const minMaxRange = Math.max(max, defaultMaxValue) - Math.min(min, 0);

    // The normalisationFactor will help us determine the % on the progress bar
    // So since we need to include the offsets here, we subtract the totalOffset instead of just have it be based on 100%
    // Double check we are not dividing by 0
    const normalisationFactor =
      minMaxRange === 0 ? 1 : (100 - totalOffset) / minMaxRange;

    let positionShift = 0;

    if (min < 0) {
      // In case there are negative values: the lowest negative value should be considered as the starting point (0 by default)
      positionShift = 100 - totalOffset;

      if (max > 0) {
        // In case there are both positive and negative values: only the negative end of the progress bar should be compsensated in the shift
        const relativeNegativeShift = Math.abs(min) / (Math.abs(min) + max);
        positionShift *= relativeNegativeShift;
      }
    }

    Object.keys(positions)
      .sort()
      .forEach((key) => {
        const plottedPoints = positions[key]!;

        if (plottedPoints.length === 0) {
          return;
        }

        plottedPoints.forEach(({ value }, index) => {
          if (index > 0) {
            offsetPosition += 2; // If there is more than one for this position, increase the offset
          }

          const pointPosition =
            normalisationFactor * value + offsetPosition + positionShift;

          dataBarPoints.push({
            isMainPoint: false,
            position: pointPosition,
            style: `left: ${pointPosition}%;`,
            value,
          });
        });

        const hasMainPoint = plottedPoints.some(
          ({ isMainPoint }) => isMainPoint,
        );

        if (hasMainPoint) {
          const indexForMiddlePoint =
            dataBarPoints.length - Math.ceil(plottedPoints.length / 2);

          const dataBarItem = dataBarPoints[indexForMiddlePoint];

          if (dataBarItem) {
            dataBarItem.isMainPoint = true;
          }
        }
      });

    return dataBarPoints;
  }

  get formattedMaxValue(): string {
    const { valueFormatter } = this.args;
    const { maxValue } = this;

    return valueFormatter?.(maxValue) ?? `${maxValue}`;
  }

  get formattedMinValue(): string {
    const { valueFormatter } = this.args;
    const { minValue } = this;

    return valueFormatter?.(minValue) ?? `${minValue}`;
  }

  // Avoid progressbars ranging from 0 to 0
  get maxValue(): number {
    const { isAbsolute } = this.args;
    const { minValue, sortedData } = this;

    // The default max value is 5 for absolute values and 5% for relative values
    const defaultMaxValue = isAbsolute ? 5 : 0.05;

    if (sortedData.length === 0) {
      return defaultMaxValue;
    }

    const maxValue = sortedData.at(-1)?.value ?? 0;

    if (minValue < 0) {
      // maxValue=0 is allowed if we are dealing with negative numbers (POS)
      return maxValue;
    }

    return Math.max(maxValue, defaultMaxValue);
  }

  get minValue(): number {
    const { sortedData } = this;

    const defaultMinValue = 0;

    if (sortedData.length === 0) {
      return defaultMinValue;
    }

    const minValue = sortedData.at(0)?.value ?? 0;

    return Math.min(minValue, defaultMinValue);
  }

  @cached get sortedData(): PlottedPoint[] {
    const { data } = this.args;

    return data.toSorted((a, b) => a.value - b.value);
  }

  <template>
    <div class={{styles.container}}>
      <p class={{styles.value}}>
        {{this.formattedMinValue}}
      </p>

      <div class={{styles.bars}}>
        <div class={{styles.bars-inner-container}}>
          {{#each this.dataBarPoints as |dataBarPoint|}}
            <div
              class={{local
                styles
                "bar"
                (if dataBarPoint.isMainPoint "main-point")
              }}
              data-test-point
              style={{trustHTML dataBarPoint.style}}
            >
            </div>
          {{/each}}
        </div>
      </div>

      <p class={{styles.value}}>
        {{this.formattedMaxValue}}
      </p>
    </div>
  </template>
}
